import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { User, Lock, Phone, ArrowRight } from 'lucide-react';
import { updateUserProfile } from '../../services/userProfile';

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editMode === 'password') {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error('كلمة المرور الجديدة غير متطابقة');
          return;
        }
      }

      const response = await updateUserProfile(user.id, {
        ...(editMode === 'name' && { full_name: formData.full_name }),
        ...(editMode === 'phone' && { phone: formData.phone }),
        ...(editMode === 'password' && { 
          current_password: formData.currentPassword,
          new_password: formData.newPassword 
        })
      });

      if (response.status === 'success') {
        toast.success('تم تحديث البيانات بنجاح');
        setEditMode('');
        setFormData({
          full_name: '',
          phone: '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error(response.message || 'حدث خطأ أثناء تحديث البيانات');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('حدث خطأ أثناء تحديث البيانات');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-4 sm:p-6 md:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mt-16 flex items-center mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/profile')}
            className=" p-2 sm:p-3 bg-blue-900  hover:bg-blue-800 rounded-xl transition-colors border border-blue-700"
          >
            <ArrowRight className=" w-5 h-5 sm:w-6 sm:h-6 text-blue-300" />
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mr-1 ">حسابي</h1>
        </div>

        {/* Settings Options */}
        <div className="space-y-4">
          {/* Name Settings */}
          <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-2xl p-6 border border-blue-700/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white text-lg font-semibold">الاسم</h3>
                  <p className="text-blue-300 text-sm">تغيير اسم الحساب</p>
                </div>
              </div>
              <button
                onClick={() => setEditMode(editMode === 'name' ? '' : 'name')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
              >
                تعديل
              </button>
            </div>
            {editMode === 'name' && (
              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div>
                  <label className="block text-blue-300 text-sm mb-2">الاسم الجديد</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full bg-black border border-blue-700/30 rounded-xl px-4 py-2 text-white"
                    placeholder="أدخل الاسم الجديد"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-xl transition-colors"
                >
                  حفظ التغييرات
                </button>
              </form>
            )}
          </div>

          {/* Phone Settings */}
          <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-2xl p-6 border border-blue-700/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white text-lg font-semibold">رقم الهاتف</h3>
                  <p className="text-blue-300 text-sm">تغيير رقم الهاتف المرتبط بالحساب</p>
                </div>
              </div>
              <button
                onClick={() => setEditMode(editMode === 'phone' ? '' : 'phone')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
              >
                تعديل
              </button>
            </div>
            {editMode === 'phone' && (
              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div>
                  <label className="block text-blue-300 text-sm mb-2">رقم الهاتف الجديد</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-black border border-blue-700/30 rounded-xl px-4 py-2 text-white"
                    placeholder="أدخل رقم الهاتف الجديد"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-xl transition-colors"
                >
                  حفظ التغييرات
                </button>
              </form>
            )}
          </div>

          {/* Password Settings */}
          <div className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 rounded-2xl p-6 border border-blue-700/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white text-lg font-semibold">كلمة المرور</h3>
                  <p className="text-blue-300 text-sm">تغيير كلمة المرور</p>
                </div>
              </div>
              <button
                onClick={() => setEditMode(editMode === 'password' ? '' : 'password')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
              >
                تعديل
              </button>
            </div>
            {editMode === 'password' && (
              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div>
                  <label className="block text-blue-300 text-sm mb-2">كلمة المرور الحالية</label>
                  <input
                    type="password"
                    value={formData.currentPassword}
                    onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                    className="w-full bg-black border border-blue-700/30 rounded-xl px-4 py-2 text-white"
                    placeholder="أدخل كلمة المرور الحالية"
                    required
                  />
                </div>
                <div>
                  <label className="block text-blue-300 text-sm mb-2">كلمة المرور الجديدة</label>
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    className="w-full bg-blue-900 border border-blue-700/30 rounded-xl px-4 py-2 text-white"
                    placeholder="أدخل كلمة المرور الجديدة"
                    required
                  />
                </div>
                <div>
                  <label className="block text-blue-300 text-sm mb-2">تأكيد كلمة المرور الجديدة</label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full bg-blue-900 border border-blue-700/30 rounded-xl px-4 py-2 text-white"
                    placeholder="أدخل كلمة المرور الجديدة مرة أخرى"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-xl transition-colors"
                >
                  حفظ التغييرات
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}